import * as React from "react";

export interface TabItem {
  key: string;
  /** ReactNode for full flexibility (icon + label + custom badge). */
  label: React.ReactNode;
  /** Optional count rendered as muted suffix `(N)`. Only when count > 0. */
  count?: number;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  /** Fired ONLY when the user actively selects a tab (click or keyboard).
   *  Safe to trigger router.push / analytics — represents user intent. */
  onChange: (key: string) => void;
  /** Fired when `value` is stale/disabled and Tabs falls back to the first
   *  enabled key. Library passes the key it would treat as active.
   *
   *  Use this to keep your state in sync WITHOUT issuing a history push or
   *  analytics event (StrictMode runs effects twice — `router.push` /
   *  event emitters here would double-fire). Recommended: `setState`,
   *  `setSearchParams(..., { replace: true })`, or `router.replace`.
   *
   *  If omitted, Tabs only adjusts its own visual highlight + focus + key
   *  nav — consumer's panel content (`{value === 'a' && ...}`) may show
   *  stale data until consumer corrects `value` themselves. Pass this
   *  callback whenever the visual/content split matters. */
  onInvalidValue?: (key: string) => void;
  /** Optional className for the outer flex container (e.g. spacing override). */
  className?: string;
  /** REQUIRED — describes the group purpose to assistive tech (e.g. "Drafts filter"). */
  "aria-label": string;
}

export function Tabs({
  items,
  value,
  onChange,
  onInvalidValue,
  className,
  "aria-label": ariaLabel,
}: TabsProps) {
  const itemRefs = React.useRef<Map<string, HTMLButtonElement | null>>(
    new Map(),
  );

  const enabledKeys = items.filter((t) => !t.disabled).map((t) => t.key);
  const valueIsValid = items.some((t) => t.key === value && !t.disabled);
  const effectiveValue = valueIsValid ? value : enabledKeys[0] ?? "";

  React.useEffect(() => {
    if (!onInvalidValue) return;
    if (effectiveValue && effectiveValue !== value) {
      onInvalidValue(effectiveValue);
    }
  }, [effectiveValue, value, onInvalidValue]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    if (enabledKeys.length === 0) return;
    e.preventDefault();
    const curIdx = enabledKeys.indexOf(effectiveValue);
    const startIdx = curIdx < 0 ? 0 : curIdx;
    const dir = e.key === "ArrowLeft" ? -1 : 1;
    const nextKey =
      enabledKeys[(startIdx + dir + enabledKeys.length) % enabledKeys.length];
    onChange(nextKey);
    itemRefs.current.get(nextKey)?.focus();
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={`flex gap-1 overflow-x-auto border-b border-border ${className ?? ""}`}
    >
      {items.map((t) => {
        const isActive = effectiveValue === t.key;
        return (
          <button
            key={t.key}
            ref={(el) => {
              if (el) itemRefs.current.set(t.key, el);
              else itemRefs.current.delete(t.key);
            }}
            type="button"
            aria-pressed={isActive}
            tabIndex={isActive ? 0 : -1}
            disabled={t.disabled}
            onClick={() => !t.disabled && onChange(t.key)}
            className={`whitespace-nowrap px-3 py-1.5 text-sm -mb-px border-b-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
              isActive
                ? "border-foreground text-foreground font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
            {t.count != null && t.count > 0 && (
              <span className="ml-1 text-xs text-muted-foreground">
                ({t.count})
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
